/**
 * A banner as the screens draw it, in the panel (banner studio, 2026-10-31; from views/Banners.tsx, where the
 * preview lived since CA-40). Used by the studio's live preview, the list's thumbnails, and «Velg et banner» in
 * Arrangementer, so a banner looks the same wherever the panel shows it.
 *
 * The ornaments are drawn HERE, by core banner-art.js from the repository — never markup that came over the network.
 * Only the text (as React text) and the layer sizes come from the server's preview, or from core's own render when the
 * row is drawn locally (faceOf). A «Bilde med tekst» banner has its photo behind the pieces, covering the box: the
 * picture still in the editor (`photoSrc`), or the saved one through the staff picture route (`photoId`).
 */
import React, { useEffect, useState } from 'react';
import { blobUrl, type Body } from '../lib/api';
import { BannerCore, type BannerRender } from '../core';

/**
 * A banner's picture in the list, in any status (banner access patch, 2026-10-04). Read from the staff-only
 * GET /admin/banners/:id/image with the session; the public picture route answers only while a banner is live, so a
 * draft or a taken-down picture would otherwise show nothing here.
 */
export function StaffPicture({ id, className = 'th', cover = false }: { id: string; className?: string; cover?: boolean }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null, gone = false;
    blobUrl(`/admin/banners/${encodeURIComponent(id)}/image`).then((u) => {
      if (gone) { if (u) URL.revokeObjectURL(u); return; }
      url = u; setSrc(u);
    });
    return () => { gone = true; if (url) URL.revokeObjectURL(url); };
  }, [id]);
  return src ? <img className={className} src={src} alt="" style={cover ? { objectFit: 'cover' } : undefined} /> : <span className="sw" aria-hidden="true" />;
}

/** The shapes a banner hangs in: a whole 16:9 screen, the half beside the times (about 8:9), or the app's card. */
export type FaceShape = 'wide' | 'split' | 'phone';

export function BannerFace({ r, template, palette, decor = null, photoId = null, photoSrc = null, thumb = false, shape = 'wide', label }: {
  r: { palette: { bg: string; fg: string }; layers: Body[]; photo?: boolean }; template: string; palette: string; decor?: string | null;
  photoId?: string | null; photoSrc?: string | null; thumb?: boolean; shape?: FaceShape; label?: string;
}) {
  const d = template ? (BannerCore.render(template, {}, { palette, decor }) || { decor: null }).decor : null;
  const photo = Boolean(r.photo) && (photoSrc || photoId);
  const lines = r.layers.map((l: Body, i: number) => (
    <div key={i} className="ly" lang={l.lang === 'ar' ? 'ar' : undefined} dir={l.dir === 'rtl' ? 'rtl' : undefined}
         style={{ fontSize: `${Number(l.sizePct)}cqw`, fontWeight: Number(l.weight), color: l.colour, ...(l.lang === 'ar' ? { lineHeight: Number(l.lineHeight) || 1.4 } : {}) }}>{l.text}</div>));
  return (
    <div className={'bnr' + (d ? ' decor' : '') + (thumb ? ' thumb' : '') + (shape !== 'wide' ? ' ' + shape : '')} aria-hidden={thumb || undefined} aria-label={label}
         style={{ background: r.palette.bg, color: r.palette.fg }}>
      {photo && (photoSrc ? <img className="bnr-photo" src={photoSrc} alt="" /> : <StaffPicture id={photoId as string} className="bnr-photo" cover />)}
      {d ? <>
        <div className="bnr-art" dangerouslySetInnerHTML={{ __html: d.html /* core banner-art.js, see above */ }} />
        <div className="bnr-in" style={{ top: `${d.inset.top}%`, right: `${d.inset.right}%`, bottom: `${d.inset.bottom}%`, left: `${d.inset.left}%` }}>{lines}</div>
      </> : <>{!thumb && <div className="safe" />}{lines}</>}
    </div>
  );
}

/** A banner row from GET /admin/banners drawn locally by core (the list, the picker): the same render the screens use. */
export function faceOf(b: Body): BannerRender | null {
  if (!b || b.template === 'image') return null;
  return BannerCore.render(b.template, (b.layers || {}) as Record<string, string>, { palette: b.palette, decor: b.decor || null });
}

/** The row as a banner thumbnail: a picture banner's picture, else core's drawing of its text. */
export function BannerThumb({ b, shape = 'wide' }: { b: Body; shape?: FaceShape }) {
  if (b.template === 'image') return <StaffPicture id={b.id} />;
  const r = faceOf(b);
  if (!r) return <span className="sw" aria-hidden="true" />;
  return <div className="bnr-thumb-wrap"><BannerFace r={r} template={b.template} palette={b.palette} decor={b.decor || null} photoId={b.template === 'photo' ? b.id : null} thumb shape={shape} /></div>;
}
